import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";

// Opens a citation's full file in a new tab. A signed cloud URL opens directly.
// A proxy-only file needs a bearer header a tab navigation can't send, so we
// fetch the bytes with auth and hand the new tab a same-origin blob URL.
export default function OpenInTabButton({ externalUrl, proxyUrl, authHeader, label, className }) {
  const [loading, setLoading] = useState(false);

  const handleOpen = useCallback(async () => {
    if (externalUrl) {
      window.open(externalUrl, "_blank", "noopener,noreferrer");
      return;
    }
    if (loading || !proxyUrl) return;
    // Open the blank tab synchronously so it stays tied to the user gesture and
    // doesn't trip the popup blocker; point it at the blob once bytes arrive.
    const win = window.open("", "_blank");
    setLoading(true);
    try {
      const headers = authHeader ? { Authorization: authHeader } : {};
      const res = await fetch(proxyUrl, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      if (win) win.location = blobUrl;
      else window.open(blobUrl, "_blank", "noopener,noreferrer");
      // Give the new tab time to load before reclaiming the object URL.
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch {
      if (win) win.close();
    } finally {
      setLoading(false);
    }
  }, [externalUrl, proxyUrl, authHeader, loading]);

  return (
    <button
      type="button"
      onClick={handleOpen}
      disabled={loading}
      className={`inline-flex items-center justify-center gap-1.5 ${className || ""}`}
    >
      {loading && (
        <svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      )}
      {loading ? "Opening…" : label}
    </button>
  );
}

OpenInTabButton.propTypes = {
  externalUrl: PropTypes.string,
  proxyUrl: PropTypes.string,
  authHeader: PropTypes.string,
  label: PropTypes.string.isRequired,
  className: PropTypes.string,
};
